/**
 * API client for the inbound blocklist: the numbers this line refuses to answer.
 *
 * Its own file rather than more lines in `callSuppression.ts`, and the reason is
 * not size. The two lists are opposites. That one records somebody else asking
 * US to stop, which is a consent record with legal weight and entries nobody
 * here may delete. This one records US refusing THEM, and every entry is our own
 * decision that any operator may undo. Sharing a module would put the two a
 * typo apart, and the failure would be silent in both directions: a customer
 * hung up on, or a robocaller filed as somebody who exercised a right.
 */
import { api } from "./api";

/** Why a number is on the list. Both values are ours - nothing here records
 *  somebody else's decision, so no entry is permanent. */
export type InboundBlockSource = "spam" | "manual";

/**
 * Who the number turned out to be, read off the calls we have had with it.
 *
 * The safeguard the screen needs most: a number that belongs to a client is
 * almost certainly not a robot. Every field can be null on its own - a number
 * blocked before it ever got through has no party at all.
 */
export interface InboundBlockParty {
  companyName: string | null;
  clientId: string | null;
  leadId: string | null;
  lastCallId: string | null;
  lastCallAt: string | null;
  calls: number;
}

export interface InboundBlock {
  id: string;
  /** E.164, the shape Twilio hands us in `From`. Anything else never matches. */
  value: string;
  source: InboundBlockSource | string;
  reason: string | null;
  note: string | null;
  createdBy: string | null;
  /** The operator who blocked it, resolved by the server. */
  createdByName: string | null;
  createdAt: string;
  /** Only filled in for a caller that asked for it (`context`). */
  party: InboundBlockParty | null;
}

/**
 * The whole list.
 *
 * `context` also asks the server who each number is, which costs two more
 * indexed reads. Only the screen asks for it - nothing else in the shell reads
 * this list, unlike the do-not-call one, because a blocked caller never reaches
 * a screen where their number could be printed.
 */
export function listInboundBlocks(opts?: { context?: boolean }): Promise<{ rows: InboundBlock[] }> {
  return api.get(`/admin/crm/calls/blocklist${opts?.context ? "?context=1" : ""}`);
}

export function addInboundBlock(input: {
  phone: string;
  source?: InboundBlockSource;
  reason?: string;
  note?: string;
}): Promise<{ block: InboundBlock }> {
  return api.post("/admin/crm/calls/blocklist", input);
}

export function removeInboundBlock(id: string): Promise<{ ok: boolean }> {
  return api.del(`/admin/crm/calls/blocklist/${encodeURIComponent(id)}`);
}
